import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { CrmDeal, CrmDealType, DealStage, DealPriority, PipelineColumn, DealAnalyticsData } from '../types';
import { Card, CardContent, Button, Badge, Tag, EmptyState, Segmented, StatsCard, Dialog } from '../components/ui';
import Layout from '../components/Layout';
import EscrowDashboard from '../components/escrow/EscrowDashboard';
import { displayDate } from '../utils/dates';

const DEAL_TYPES: { value: CrmDealType; label: string }[] = [
  { value: 'sale', label: 'Sale' }, { value: 'lease', label: 'Lease' },
  { value: 'listing', label: 'Listing' }, { value: 'acquisition', label: 'Acquisition' },
  { value: 'disposition', label: 'Disposition' },
];

const STAGES: { value: DealStage; label: string }[] = [
  { value: 'prospecting', label: 'Prospecting' }, { value: 'qualification', label: 'Qualification' },
  { value: 'proposal', label: 'Proposal' }, { value: 'negotiation', label: 'Negotiation' },
  { value: 'under_contract', label: 'Under Contract' }, { value: 'due_diligence', label: 'Due Diligence' },
  { value: 'closing', label: 'Closing' }, { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

const PRIORITIES: { value: DealPriority; label: string }[] = [
  { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
];

const stageBadgeVariant = (stage: DealStage) => {
  const map: Record<string, 'info' | 'success' | 'warning' | 'error' | 'primary' | 'default'> = {
    prospecting: 'default', qualification: 'info', proposal: 'info',
    negotiation: 'warning', under_contract: 'primary', due_diligence: 'warning',
    closing: 'primary', closed_won: 'success', closed_lost: 'error',
  };
  return map[stage] || 'default';
};

const priorityBadgeVariant = (p: DealPriority) => {
  const map: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
    low: 'default', medium: 'info', high: 'warning', urgent: 'error',
  };
  return map[p] || 'default';
};

const formatCurrency = (v?: number) => {
  if (!v) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

type ViewMode = 'escrow' | 'list' | 'pipeline';

export default function Deals() {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>('escrow');
  const [deals, setDeals] = useState<CrmDeal[]>([]);
  const [pipeline, setPipeline] = useState<PipelineColumn[]>([]);
  const [analytics, setAnalytics] = useState<DealAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    deal_name: '', deal_type: 'sale' as CrmDealType, stage: 'prospecting' as DealStage,
    deal_value: '', priority: 'medium' as DealPriority, description: '',
    expected_close_date: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'escrow') {
        setLoading(false);
        return;
      } else if (view === 'pipeline') {
        const [pipeRes, analyticsRes] = await Promise.all([
          api.getCrmDealPipeline(),
          api.getCrmDealAnalytics(),
        ]);
        setPipeline(pipeRes.pipeline || []);
        setAnalytics(analyticsRes.analytics || null);
      } else {
        const params: Record<string, string> = {};
        if (search) params.search = search;
        if (stageFilter) params.stage = stageFilter;
        const res = await api.listCrmDeals(params);
        setDeals(res.deals || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [view, search, stageFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // OO1b: this page READS. It used to POST /ghl-sync/opportunities/refresh on mount to
  // pull newly under-contract opps into Escrow, which made opening a list write to
  // production. The server already does exactly that work on its own schedule -
  // `startOpportunitySyncCron` (backend/src/services/scheduler.ts) runs
  // syncOpportunities + autoPromoteUnderContract every 15 minutes - so the mount POST
  // bought nothing and cost a write. Refreshing the mirror on demand belongs to the
  // GHL Sync page's own button, not to opening a list.

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: any = { ...form };
      if (payload.deal_value) payload.deal_value = parseFloat(payload.deal_value);
      else delete payload.deal_value;
      if (!payload.expected_close_date) delete payload.expected_close_date;
      if (!payload.description) delete payload.description;
      await api.createCrmDeal(payload);
      setShowModal(false);
      setForm({ deal_name: '', deal_type: 'sale', stage: 'prospecting', deal_value: '', priority: 'medium', description: '', expected_close_date: '' });
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create deal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      {/* ===== Header (OO5): the Oswald title, one sub line, the design's filled-segment view switch,
          the one red action. On the page ground - no white band, no second max-width. ===== */}
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <div className="min-w-0 flex-1 basis-[320px]">
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Escrow / Transactions</h1>
          <div className="mt-[7px] text-[13.5px] leading-snug text-gray-700">Deals in escrow with deadline countdowns · Pipeline and List show every stage</div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:shrink-0">
          <Segmented<ViewMode>
            aria-label="View"
            value={view}
            onChange={setView}
            options={[
              { value: 'escrow', label: 'Escrow' },
              { value: 'pipeline', label: 'Pipeline' },
              { value: 'list', label: 'List' },
            ]}
          />
          <Button onClick={() => setShowModal(true)}>+ New Deal</Button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-700">{error}</div>
      )}

      {/* Analytics cards */}
      {view === 'pipeline' && analytics && (
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatsCard label="Active deals" value={analytics.active_deals} />
          <StatsCard label="Pipeline value" value={formatCurrency(analytics.total_pipeline_value)} />
          <StatsCard label="Win rate" value={`${analytics.win_rate}%`} />
          <StatsCard label="Commission earned" value={formatCurrency(analytics.total_commission_earned)} />
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="py-12 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500" />
            <p className="mt-2 text-[12.5px] text-gray-500">Loading deals...</p>
          </div>
        ) : view === 'escrow' ? (
          <EscrowDashboard />
        ) : view === 'pipeline' ? (
          /* Pipeline / Kanban View */
          <div className="flex gap-4 overflow-x-auto pb-4">
            {pipeline.map((col) => (
              <div key={col.stage} className="w-72 flex-shrink-0 rounded-xl bg-gray-100 p-2.5">
                <div className="mb-2 flex items-center justify-between border-b border-gray-200 px-1 pb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="truncate text-[9.5px] font-bold uppercase tracking-[.13em] text-gray-500">
                      {col.stage.replace(/_/g, ' ')}
                    </h3>
                    <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[11px] tabular-nums text-gray-700">{col.count}</span>
                  </div>
                  <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">{formatCurrency(col.total_value)}</span>
                </div>
                <div className="space-y-2 min-h-[100px]">
                  {col.deals.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No deals</p>
                  ) : (
                    col.deals.map((deal: CrmDeal) => (
                      <div
                        key={deal.id}
                        className="cursor-pointer rounded-xl border border-gray-200 bg-white p-3 transition-colors hover:border-gray-900"
                        onClick={() => navigate(`/crm/deals/${deal.id}`)}
                      >
                        <p className="truncate text-[13px] font-medium text-gray-800">{deal.deal_name}</p>
                        <div className="flex items-center justify-between mt-2">
                          <Badge size="sm" variant={priorityBadgeVariant(deal.priority)}>{deal.priority}</Badge>
                          <span className="text-[13px] font-semibold tabular-nums text-gray-800">{formatCurrency(deal.deal_value)}</span>
                        </div>
                        {deal.expected_close_date && (
                          <p className="mt-1 text-[11.5px] text-gray-500">
                            Close: {displayDate(deal.expected_close_date)}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <>
            <div className="mb-4 flex flex-wrap gap-3">
              <form onSubmit={(e) => { e.preventDefault(); fetchData(); }} className="flex gap-2 flex-1 min-w-[280px]">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search deals..."
                  aria-label="Search deals"
                  className="h-9 flex-1 rounded-lg border-gray-300 text-[12.5px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                />
                <Button type="submit" variant="outline">Search</Button>
              </form>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                aria-label="Stage"
                className="h-9 rounded-lg border-gray-300 py-0 pl-3 pr-8 text-[12.5px] text-gray-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
              >
                <option value="">All Stages</option>
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <Card>
              <CardContent className="p-0">
                {deals.length === 0 ? (
                  <EmptyState
                    title="No deals found"
                    description="Create your first deal to start tracking your pipeline."
                    action={{ label: 'New Deal', onClick: () => setShowModal(true) }}
                  />
                ) : (
                  <div className="overflow-x-auto">
                    {/* The design's grid table (OO5): uppercase 9.5px heads on #FAFAFA, hairline rows,
                        tabular numbers, the type as a TAG, stage and priority as pills. */}
                    <table className="w-full min-w-[760px] text-[13px]">
                      <thead className="border-b border-gray-150 bg-gray-50">
                        <tr className="text-[9.5px] font-bold uppercase leading-none tracking-[.12em] text-gray-500">
                          <th className="px-4 py-2.5 text-left">Deal Name</th>
                          <th className="px-4 py-2.5 text-left">Type</th>
                          <th className="px-4 py-2.5 text-left">Stage</th>
                          <th className="px-4 py-2.5 text-left">Priority</th>
                          <th className="px-4 py-2.5 text-right">Value</th>
                          <th className="px-4 py-2.5 text-left">Expected Close</th>
                          <th className="px-4 py-2.5 text-left">Assigned To</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {deals.map((deal) => (
                          <tr
                            key={deal.id}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => navigate(`/crm/deals/${deal.id}`)}
                          >
                            <td className="px-4 py-3 font-medium text-gray-800">{deal.deal_name}</td>
                            <td className="px-4 py-3">
                              <Tag>{deal.deal_type}</Tag>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={stageBadgeVariant(deal.stage)}>
                                {deal.stage?.replace(/_/g, ' ')}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={priorityBadgeVariant(deal.priority)}>{deal.priority}</Badge>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-800">{formatCurrency(deal.deal_value)}</td>
                            <td className="px-4 py-3 tabular-nums text-gray-700">
                              {displayDate(deal.expected_close_date) ?? '-'}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {deal.assigned_user?.full_name || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* New Deal Modal */}
      {/* OO5c: through the one Dialog primitive - the card's language, 6px, the 15px head,
          the body as the scroll container. */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title="New Deal"
        size="lg"
        onSubmit={handleCreate}
        footer={<>
          <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Deal'}</Button>
        </>}
      >
        <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deal Name *</label>
                <input required value={form.deal_name} onChange={(e) => setForm(f => ({ ...f, deal_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal Type</label>
                  <select value={form.deal_type} onChange={(e) => setForm(f => ({ ...f, deal_type: e.target.value as CrmDealType }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                  <select value={form.stage} onChange={(e) => setForm(f => ({ ...f, stage: e.target.value as DealStage }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value ($)</label>
                  <input type="number" value={form.deal_value} onChange={(e) => setForm(f => ({ ...f, deal_value: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value as DealPriority }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close Date</label>
                <input type="date" value={form.expected_close_date} onChange={(e) => setForm(f => ({ ...f, expected_close_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
        </div>
      </Dialog>
    </Layout>
  );
}
